
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Search, Heart, MessageCircle, User, BarChart3, Plus, Calendar, MapPin } from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { trackEvent } from '../services/analyticsService';
import { isLandlord as checkIsLandlord } from '../utils/roleHelpers';
import { logger } from '../services/productionLogger';

interface MobileBottomNavProps { forceRole?: 'landlord' | 'renter'; showOnDesktop?: boolean }
const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ forceRole, showOnDesktop }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();

  // Debug logging
  if (import.meta.env.DEV) {
    logger.debug('[MobileBottomNav] Rendering', {
      user: !!user,
      profile: !!profile,
      roles: profile?.roles,
      forceRole
    });
  }

  if (!user) {
    return null;
  }

  if (!profile && !forceRole) {
    return null;
  }

  const isLandlord = (forceRole ? forceRole === 'landlord' : checkIsLandlord(profile));

  const landlordNavItems = [
    { icon: Home, label: 'Dashboard', path: '/landlord-dashboard', gradient: 'from-blue-500 to-indigo-500' },
    { icon: BarChart3, label: 'Analytics', path: '/landlord-analytics', gradient: 'from-violet-500 to-purple-500' },
    { icon: Plus, label: 'Add Listing', path: '/add-listing', gradient: 'from-green-500 to-emerald-500' },
    { icon: MessageCircle, label: 'Messages', path: '/landlord-messages', gradient: 'from-purple-500 to-violet-500' },
    { icon: Calendar, label: 'Tours', path: '/landlord-tours', gradient: 'from-teal-500 to-cyan-500' }
  ];

  // Renter navigation items with Dashboard and Map
  const renterNavItems = [
    { icon: Home, label: 'Dashboard', path: '/dashboard', gradient: 'from-blue-500 to-cyan-500' },
    { icon: Search, label: 'Discover', path: '/discover', gradient: 'from-green-500 to-teal-500' },
    { icon: Heart, label: 'Matches', path: '/matches', gradient: 'from-pink-500 to-red-500' },
    { icon: MessageCircle, label: 'Messages', path: '/messages', gradient: 'from-purple-500 to-indigo-500' },
    { icon: MapPin, label: 'Map', path: '/map', gradient: 'from-emerald-500 to-teal-500' }
  ];

  const navItems = isLandlord ? landlordNavItems : renterNavItems;

  if (import.meta.env.DEV) {
    logger.debug('[MobileBottomNav] Using nav items', { 
      type: isLandlord ? 'landlord' : 'renter', 
      count: navItems.length 
    });
  }

  return (
    <>
      {/* Spacer to prevent content overlap - responsive to visibility */}
      <div className={cn(showOnDesktop ? 'hidden lg:block h-16' : 'h-16 lg:h-0')} />
      
      {/* Bottom Navigation - thinner design matching header */}
      <div className={cn('fixed bottom-0 left-0 right-0 z-50 h-16', showOnDesktop ? 'hidden lg:block' : 'lg:hidden')}>
        {/* Sophisticated backdrop with gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/90 to-slate-900/70 backdrop-blur-xl" />
        
        {/* Glass effect border */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        
        {/* Navigation content */}
        <div className="relative h-full flex items-center">
          <nav className="flex items-center justify-around w-full px-4 max-w-md mx-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    try {
                      trackEvent('bottom_nav_click', {
                        role: isLandlord ? 'landlord' : 'renter',
                        label: item.label,
                        path: item.path
                      });
                    } catch (e) {
                      logger.warn('[MobileBottomNav] Analytics tracking failed', { error: e });
                    }
                  }}
                  aria-label={`Go to ${item.label}`}
                  aria-current={isActive ? 'page' : undefined}
                  title={item.label}
                  className={cn(
                    'flex flex-col items-center justify-center transition-all duration-300 group relative',
                    'min-w-[56px] py-1 px-1.5 rounded-xl',
                    isActive ? 'scale-105' : 'hover:scale-105'
                  )}
                >
                  {/* Active background glow */}
                  {isActive && (
                    <>
                      <div className={cn(
                        'absolute inset-0 rounded-xl bg-gradient-to-br opacity-20 blur-sm',
                        item.gradient
                      )} />
                      <div className={cn(
                        'absolute inset-0 rounded-xl bg-gradient-to-br opacity-10',
                        item.gradient
                      )} />
                    </>
                  )}
                  
                  {/* Icon container - optimized for thinner bar */}
                  <div className={cn(
                    'relative z-10 w-9 h-9 rounded-lg flex items-center justify-center mb-0.5',
                    'transition-all duration-300 shadow-md',
                    isActive 
                      ? `bg-gradient-to-br ${item.gradient} shadow-lg` 
                      : 'bg-slate-800/60 border border-slate-700/50 group-hover:bg-slate-700/60 group-hover:border-slate-600/50'
                  )}>
                    <Icon className={cn(
                      'w-4 h-4 transition-all duration-300',
                      isActive 
                        ? 'text-white drop-shadow-sm' 
                        : 'text-slate-400 group-hover:text-slate-200'
                    )} />
                    
                    {/* Active pulse effect */}
                    {isActive && (
                      <div className={cn(
                        'absolute inset-0 rounded-lg bg-gradient-to-br animate-pulse opacity-30',
                        item.gradient
                      )} />
                    )}
                  </div>
                  
                  {/* Label - optimized text size for thinner bar */}
                  <span className={cn(
                    'text-[11px] font-medium transition-all duration-300 tracking-wide leading-none',
                    isActive 
                      ? 'text-white font-semibold' 
                      : 'text-slate-400 group-hover:text-slate-200'
                  )}>
                    {item.label}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>
    </>
  );
};

export default MobileBottomNav;
